import {
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { GlobalService, Office, User } from '../../../core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  CommodityService,
  IOfficeRateMarkup,
  IOfficeRateMarkupBracket,
  IOfficeRateMarkupSetting,
  IWeightTypeRate,
  OfficeService,
} from '@cai-services';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { isNumberValidator } from '../../../validators';
import { PaginationInstance } from 'ngx-pagination';

@Component({
  selector: 'cai-office-rate-markup',
  templateUrl: './office-rate-markup.component.html',
  styleUrls: ['./office-rate-markup.component.scss'],
  standalone: false,
})
export class OfficeRateMarkupComponent implements OnInit, OnChanges {
  @ViewChild('deleteModal', { read: TemplateRef })
  deleteModal: TemplateRef<any>;
  @ViewChild('rightSideNav', { static: true }) rightSideNav: ElementRef;

  @Input() activeOffice: Office;
  @Input() currentUser: User;

  deleteModalMessage: string;
  asideContent:
    | 'create-setting'
    | 'update-setting'
    | 'view-markup'
    | 'create-markup'
    | 'update-markup'
    | undefined;

  markupForm: FormGroup;
  markupSettingForm: FormGroup;

  rateMarkupSettings: IOfficeRateMarkupSetting[] = [];

  airlineOptions: { value: string | number; label: string }[] = [];
  airlineValueToLabelMap: { [value: string]: string } = {};
  commodityOptions: { value: string | number; label: string }[] = [];
  commodityValueToLabelMap: { [value: string]: string } = {};
  airportOptions: { value: string | number; label: string }[] = [];
  airportValueToLabelMap: { [value: string]: string } = {};
  rateWeightTypes: IWeightTypeRate[] = [];
  rateCurrencies: string[];
  defaultCurrencyCode: string;
  isSavingSetting: boolean = false;
  isSavingRecord: boolean = false;

  protected config: PaginationInstance = {
    itemsPerPage: 20,
    currentPage: 1,
  };

  readonly selectedMarkup = signal<IOfficeRateMarkup | undefined>(undefined);
  readonly selectedMarkupOnlyAirlines = computed(
    () =>
      this.selectedMarkup()?.onlyAirlines?.map(
        (value) => this.airlineValueToLabelMap[value],
      ) ?? [],
  );
  readonly selectedMarkupOnlyCommodities = computed(
    () =>
      this.selectedMarkup()?.onlyCommodities?.map(
        (value) => this.commodityValueToLabelMap[value],
      ) ?? [],
  );
  readonly selectedMarkupOnlyDestinationAirports = computed(
    () =>
      this.selectedMarkup()?.onlyDestinationAirports?.map(
        (value) => this.airportValueToLabelMap[value],
      ) ?? [],
  );
  readonly selectedMarkupNotAirlines = computed(
    () =>
      this.selectedMarkup()?.notAirlines?.map(
        (value) => this.airlineValueToLabelMap[value],
      ) ?? [],
  );
  readonly selectedMarkupNotCommodities = computed(
    () =>
      this.selectedMarkup()?.notCommodities?.map(
        (value) => this.commodityValueToLabelMap[value],
      ) ?? [],
  );
  readonly selectedMarkupNotDestinationAirports = computed(
    () =>
      this.selectedMarkup()?.notDestinationAirports?.map(
        (value) => this.airportValueToLabelMap[value],
      ) ?? [],
  );
  readonly selectedMarkupSettingId = signal<number | undefined>(undefined);
  readonly applyToExpanded = signal<boolean>(true);
  readonly excludeExpanded = signal<boolean>(false);

  private readonly markupFlatValidators: ValidatorFn[] = [
    Validators.required,
    isNumberValidator(),
    Validators.min(0),
  ];
  private readonly markupPercentValidators: ValidatorFn[] = [
    ...this.markupFlatValidators,
    Validators.max(100),
  ];
  private readonly includeChipControlNames: string[] = [
    'onlyAirlines',
    'onlyCommodities',
    'onlyDestinationAirports',
  ];
  private readonly excludeChipControlNames: string[] = [
    'notAirlines',
    'notCommodities',
    'notDestinationAirports',
  ];
  private readonly chipControlNames: string[] = [
    ...this.includeChipControlNames,
    ...this.excludeChipControlNames,
  ];

  constructor(
    private readonly globalService: GlobalService,
    private readonly commodityService: CommodityService,
    private readonly officeService: OfficeService,
    private readonly fb: FormBuilder,
    private readonly modalService: NgbModal,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.markupForm = this.fb.group({
      id: [],
      markupSettingId: [],
      markupCurrencyCode: [],
      applyToAllWeightBreaks: [],
      markupBrackets: this.fb.array([]),
      onlyAirlines: [[]],
      onlyCommodities: [[]],
      onlyDestinationAirports: [[]],
      notAirlines: [[]],
      notCommodities: [[]],
      notDestinationAirports: [[]],
      createAnother: [false],
    });
    this.markupSettingForm = this.fb.group({
      id: [],
      name: ['', [Validators.required]],
    });
  }

  async ngOnInit() {
    const allAirlines = await this.globalService.getAirlineCompanies();
    this.airlineOptions = allAirlines.map((airline) => ({
      value: airline.airlineCompanyCode,
      label: airline.airlineCompanyCode + ' - ' + airline.airlineCompanyName,
    }));
    this.airlineOptions.forEach(
      (option) => (this.airlineValueToLabelMap[option.value] = option.label),
    );
    const topLevelCommodities =
      await this.commodityService.getCommoditiesByLevel(1, true);
    this.commodityOptions = topLevelCommodities.map((commodity) => ({
      value: commodity.id,
      label: commodity.name,
    }));
    this.commodityOptions.forEach(
      (option) => (this.commodityValueToLabelMap[option.value] = option.label),
    );
    const allAirports = await this.globalService.getAirports();
    this.airportOptions = allAirports.map((airport) => ({
      value: airport.airportCode,
      label: airport.airportCode + ' - ' + airport.airportName,
    }));
    this.airportOptions.forEach(
      (option) => (this.airportValueToLabelMap[option.value] = option.label),
    );

    this.rateWeightTypes = await this.globalService.getRateWeightTypes();
    this.rateWeightTypes.forEach((weightType) =>
      this.markupBracketsArray.push(this.initMarkupBracketGroup(weightType)),
    );
    this.markupAllWeightControl.valueChanges.subscribe((value) =>
      this.onMarkupAllWeightControlChange(value),
    );
    this.chipControlNames.forEach((chipsControlName) =>
      this.markupForm.controls[chipsControlName].valueChanges.subscribe((_) =>
        this.onChipsChange(),
      ),
    );
  }

  private initMarkupBracketGroup(weightType: IWeightTypeRate): FormGroup {
    const markupBracketGroup = this.fb.group({
      id: [],
      weightType: [weightType],
      markupAmount: ['', this.markupPercentValidators],
      isMarkupPercent: [true],
    });

    markupBracketGroup.controls['isMarkupPercent'].valueChanges.subscribe(
      (value) => this.onMarkupIsPercentChange(markupBracketGroup, value),
    );
    return markupBracketGroup;
  }

  private onMarkupAllWeightControlChange(applyToAllWeightBreak: boolean): void {
    for (let i = 0; i < this.markupBracketsArray.length; i++) {
      const markupBracketGroup = this.markupBracketsArray.at(i) as FormGroup;
      const { weightType } = markupBracketGroup.controls['weightType']
        .value as IWeightTypeRate;
      if (weightType !== 'KG') {
        continue;
      }

      if (applyToAllWeightBreak) {
        markupBracketGroup.controls['markupAmount'].clearValidators();
        markupBracketGroup.controls['markupAmount'].updateValueAndValidity();
      } else {
        this.onMarkupIsPercentChange(
          markupBracketGroup,
          markupBracketGroup.controls['isMarkupPercent'].value,
        );
      }
    }
  }

  private onMarkupIsPercentChange(
    markupBracketGroup: FormGroup,
    isPercent: boolean,
  ): void {
    if (isPercent) {
      markupBracketGroup.controls['markupAmount'].setValidators(
        this.markupPercentValidators,
      );
    } else {
      markupBracketGroup.controls['markupAmount'].setValidators(
        this.markupFlatValidators,
      );
    }
    markupBracketGroup.controls['markupAmount'].updateValueAndValidity();
  }

  private onChipsChange(): void {
    const markup = this.markupForm.value;
    if (this.isMarkupApplyTo(markup)) {
      this.excludeChipControlNames
        .filter(
          (excludeChipControlName) =>
            this.markupForm.controls[excludeChipControlName].enabled,
        )
        .forEach((excludeChipControlName) =>
          this.markupForm.controls[excludeChipControlName].disable(),
        );
    } else if (this.isMarkupExclude(markup)) {
      this.includeChipControlNames
        .filter(
          (applyChipControlName) =>
            this.markupForm.controls[applyChipControlName].enabled,
        )
        .forEach((applyChipControlName) =>
          this.markupForm.controls[applyChipControlName].disable(),
        );
    } else {
      this.chipControlNames
        .filter(
          (chipControlName) =>
            this.markupForm.controls[chipControlName].disabled,
        )
        .forEach((chipControlName) =>
          this.markupForm.controls[chipControlName].enable(),
        );
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hasOwnProperty('activeOffice')) {
      this.onOfficeChange();
    }
  }

  async onOfficeChange(): Promise<void> {
    this.rateCurrencies = await this.officeService.getCurrenciesOfOfficeRates(
      this.activeOffice.id,
    );
    if (this.rateCurrencies.length === 0) {
      this.rateCurrencies = [
        this.activeOffice.country.defaultCurrency.currencyCode,
      ];
    }
    this.defaultCurrencyCode = this.rateCurrencies[0];
    this.rateMarkupSettings = await this.fetchOfficeMarkupSettings();
    this.asideContent = undefined;
    this.cdr.detectChanges();
  }

  async fetchOfficeMarkupSettings(): Promise<IOfficeRateMarkupSetting[]> {
    const pagedSettings = await this.officeService.getRateMarkupSettings(
      this.activeOffice.id,
      this.config.currentPage - 1,
    );
    const settings = pagedSettings.content;
    settings.forEach((setting) => (setting.rateMarkups = undefined));
    this.config.totalItems = pagedSettings.totalElements;
    return settings;
  }

  onClickCreateSetting(): void {
    this.markupSettingForm.reset();
    this.asideContent = 'create-setting';
  }

  onClickEditSetting(setting: IOfficeRateMarkupSetting, e: MouseEvent): void {
    e.stopPropagation();
    this.markupSettingForm.patchValue({
      id: setting.id,
      name: setting.name,
    });
    this.asideContent = 'update-setting';
  }

  async onSaveEditSetting(): Promise<void> {
    this.isSavingSetting = true;
    try {
      const createdSetting =
        await this.officeService.createOrUpdateOfficeRateMarkupSetting(
          this.activeOffice.id,
          this.markupSettingForm.value,
        );
      this.rateMarkupSettings = await this.fetchOfficeMarkupSettings();
      this.onClickCreateMarkupRecord(createdSetting);
      this.cdr.detectChanges();
    } catch {}
    this.isSavingSetting = false;
  }

  private async refreshMarkupSettings() {
    this.asideContent = undefined;
    this.rateMarkupSettings = await this.fetchOfficeMarkupSettings();
    this.cdr.detectChanges();
  }

  onClickCreateMarkupRecord(markupSetting: IOfficeRateMarkupSetting): void {
    this.asideContent = 'create-markup';
    this.markupForm.patchValue({
      id: undefined,
      markupSettingId: markupSetting.id,
      markupCurrencyCode: this.defaultCurrencyCode,
      applyToAllWeightBreaks: false,
      onlyAirlines: [],
      onlyCommodities: [],
      onlyDestinationAirports: [],
      notAirlines: [],
      notCommodities: [],
      notDestinationAirports: [],
      createAnother: false,
    });
    this.defaultMarkupBrackets.forEach((defaultValue, index) =>
      this.markupBracketsArray.at(index).patchValue(defaultValue),
    );
    this.onChipsChange();
  }

  onClickEditMarkup(
    markup: IOfficeRateMarkup,
    e: MouseEvent,
    markupSetting?: IOfficeRateMarkupSetting,
  ): void {
    e.stopPropagation();
    this.markupForm.patchValue({
      ...markup,
      markupSettingId: markupSetting?.id ?? this.selectedMarkupSettingId(),
      markupCurrencyCode: markup.markupCurrency.currencyCode,
      createAnother: false,
    });
    markup.markupBrackets.forEach((bracket, index) =>
      this.markupBracketsArray.at(index).patchValue(bracket),
    );
    this.asideContent = 'update-markup';
    this.onChipsChange();
  }

  onClickDuplicateMarkup(
    markup: IOfficeRateMarkup,
    e: MouseEvent,
    markupSetting?: IOfficeRateMarkupSetting,
  ): void {
    e.stopPropagation();
    this.markupForm.patchValue({
      ...markup,
      id: undefined,
      markupSettingId: markupSetting?.id ?? this.selectedMarkupSettingId(),
      markupCurrencyCode: markup.markupCurrency.currencyCode,
      createAnother: false,
    });
    markup.markupBrackets.forEach((bracket, index) =>
      this.markupBracketsArray.at(index).patchValue({
        ...bracket,
        id: undefined,
      }),
    );
    this.asideContent = 'create-markup';
    this.onChipsChange();
  }

  async onSaveEditMarkup(): Promise<void> {
    this.isSavingRecord = true;
    const markupToCreate = this.markupForm.value;

    markupToCreate.markupBrackets.forEach((bracket, index) => {
      bracket.weightType = this.rateWeightTypes[index];
      bracket.flatAmount = bracket.isMarkupPercent ? 0 : bracket.markupAmount;
      bracket.percentageAmount = bracket.isMarkupPercent
        ? bracket.markupAmount / 100
        : 0;
    });
    markupToCreate.onlyAirlines ??= [];
    markupToCreate.onlyCommodities ??= [];
    markupToCreate.onlyDestinationAirports ??= [];
    markupToCreate.notAirlines ??= [];
    markupToCreate.notCommodities ??= [];
    markupToCreate.notDestinationAirports ??= [];

    // if apply to all weight breaks, copy nominal to all weight breaks
    if (markupToCreate.applyToAllWeightBreaks) {
      const nominalBracket = markupToCreate.markupBrackets.find(
        (bracket) => bracket.weightType.weightType === 'N',
      );
      markupToCreate.markupBrackets
        .filter((bracket) => bracket.weightType.weightType === 'KG')
        .forEach((bracket) => {
          bracket.flatAmount = nominalBracket.flatAmount;
          bracket.percentageAmount = nominalBracket.percentageAmount;
        });
    }

    markupToCreate.markupCurrency = {
      currencyCode: markupToCreate['markupCurrencyCode'],
    } as any;
    try {
      const markupSettingId = markupToCreate['markupSettingId'];
      const createdRecord =
        await this.officeService.createOrUpdateOfficeRateMarkup(
          this.activeOffice.id,
          markupSettingId,
          markupToCreate,
        );
      this.rateMarkupSettings = await this.fetchOfficeMarkupSettings();
      const updatedSetting = this.rateMarkupSettings.find(
        (setting) => setting.id === markupSettingId,
      );
      if (!markupToCreate['createAnother']) {
        this.selectedMarkup.set(
          updatedSetting.rateMarkups.find(
            (markup) => markup.id === createdRecord.id,
          ),
        );
        this.asideContent = 'view-markup';
      } else {
        this.onClickCreateMarkupRecord(updatedSetting);
        // reset scroll on create record side nav
        this.rightSideNav.nativeElement.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
      this.cdr.detectChanges();
    } catch {}
    this.isSavingRecord = false;
    this.markupForm.markAsUntouched();
  }

  get markupBracketsArray(): FormArray {
    return this.markupForm.get('markupBrackets') as FormArray;
  }

  get markupAllWeightControl(): AbstractControl {
    return this.markupForm.controls['applyToAllWeightBreaks'];
  }

  get markupCurrencyControl(): AbstractControl {
    return this.markupForm.controls['markupCurrencyCode'];
  }

  get isAsideOpen(): boolean {
    return this.asideContent !== undefined;
  }

  get defaultMarkupBrackets(): IOfficeRateMarkupBracket[] {
    return this.rateWeightTypes.map(
      (weightType) =>
        ({
          id: undefined,
          weightType,
          markupAmount: undefined,
          isMarkupPercent: true,
        }) as any,
    );
  }

  weightTypeLabel(
    rateWeightType: IWeightTypeRate,
    applyToAllWeightBreaks: boolean,
  ) {
    if (rateWeightType.weightType === 'M') {
      return 'Min. charge';
    }
    if (rateWeightType.weightType === 'N' && applyToAllWeightBreaks) {
      return 'All weight breaks';
    }
    if (rateWeightType.weightType === 'N' && !applyToAllWeightBreaks) {
      return 'Nom.';
    }

    return rateWeightType.weight + 'KG';
  }

  amountSuffix(
    markup: IOfficeRateMarkup,
    bracket: IOfficeRateMarkupBracket,
  ): string {
    if (bracket.isMarkupPercent) {
      return '%';
    }

    if (bracket.weightType.weightType === 'M') {
      return markup.markupCurrency.currencyCode;
    }

    return markup.markupCurrency.currencyCode + '/KG';
  }

  viewRateMarkup(
    markupSetting: IOfficeRateMarkupSetting,
    markup: IOfficeRateMarkup,
  ): void {
    this.asideContent = 'view-markup';
    this.selectedMarkupSettingId.set(markupSetting.id);
    this.selectedMarkup.set(markup);
  }

  async openDeleteSettingModal(
    markupSetting: IOfficeRateMarkupSetting,
    e: MouseEvent,
  ): Promise<void> {
    e.stopPropagation();
    this.deleteModalMessage =
      'Do you really want to delete this setting configuration? This action cannot be undone.';
    try {
      await this.modalService.open(this.deleteModal, { centered: true }).result;
      await this.officeService.deleteOfficeRateMarkupSetting(
        this.activeOffice.id,
        markupSetting.id,
      );
      await this.refreshMarkupSettings();
    } catch {}
  }

  async openDeleteRecordModal(
    markup: IOfficeRateMarkup,
    e: MouseEvent,
    markupSetting?: IOfficeRateMarkupSetting,
  ): Promise<void> {
    e.stopPropagation();
    this.deleteModalMessage =
      'Do you really want to delete this record? This action cannot be undone.';
    try {
      await this.modalService.open(this.deleteModal, { centered: true }).result;
      await this.officeService.deleteOfficeRateMarkup(
        this.activeOffice.id,
        markupSetting?.id ?? this.selectedMarkupSettingId(),
        markup.id,
      );
      await this.refreshMarkupSettings();
    } catch {}
  }

  bracketErrorMessage(bracketGroup: any): string {
    const errors = bracketGroup.controls['markupAmount'].errors;
    const isMarkupPercent = bracketGroup.controls['isMarkupPercent'].value;
    if (errors['required']) {
      return 'Markup is required.';
    }
    if (isMarkupPercent) {
      return 'Markup must be between 0% and 100%.';
    }
    if (!isMarkupPercent) {
      return 'Markup must be larger than 0.';
    }
  }

  isMarkupApplyTo(rateMarkup: IOfficeRateMarkup): boolean {
    return (
      (rateMarkup.onlyAirlines ?? []).length !== 0 ||
      (rateMarkup.onlyCommodities ?? []).length !== 0 ||
      (rateMarkup.onlyDestinationAirports ?? []).length !== 0
    );
  }

  isMarkupExclude(rateMarkup: IOfficeRateMarkup): boolean {
    return (
      (rateMarkup.notAirlines ?? []).length !== 0 ||
      (rateMarkup.notCommodities ?? []).length !== 0 ||
      (rateMarkup.notDestinationAirports ?? []).length !== 0
    );
  }

  protected async onPageChange(number: number): Promise<void> {
    this.config.currentPage = number;
    this.rateMarkupSettings = await this.fetchOfficeMarkupSettings();
    this.asideContent = undefined;
    this.cdr.detectChanges();
  }

  protected async onExpandMarkupSetting(
    rateMarkupSetting: IOfficeRateMarkupSetting & { expanded: boolean },
  ): Promise<void> {
    await this.fetchMarkupsOfSetting(rateMarkupSetting);
    rateMarkupSetting.expanded = !rateMarkupSetting.expanded;
    this.cdr.detectChanges();
  }

  private async fetchMarkupsOfSetting(
    rateMarkupSetting: IOfficeRateMarkupSetting & { expanded: boolean },
  ): Promise<void> {
    if (rateMarkupSetting.rateMarkups !== undefined) {
      return;
    }

    rateMarkupSetting.rateMarkups = (
      await this.officeService.getMarkupsOfSetting(
        this.activeOffice.id,
        rateMarkupSetting.id,
      )
    ).content;
    rateMarkupSetting.rateMarkups.forEach((markup) =>
      markup.markupBrackets.forEach((bracket) => {
        bracket.isMarkupPercent = bracket.percentageAmount !== 0;
        bracket.markupAmount = bracket.isMarkupPercent
          ? Math.round(bracket.percentageAmount * 1000000) / 10000
          : bracket.flatAmount;
      }),
    );
  }
}
